import { Button, Container, FlexGrid, Header, Image, Modal, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import useImageSrcBuilder from '../../../lib/hooks/useImageSrcBuilder';
import generateCMSMetaTags from '../../../lib/utils/generateCMSMetaTags';
import { AboutUsPagePropType } from '../../prop-types/cms-page';
import GradientBanner from '../GradientBanner';
import Markup from '../Markup';
import Page from '../Page';
import styles from './AboutUsPage.module.css';

const cx = classnames.bind(styles);

function buildTeamMember(teamMember = {}) {
  const { firstName, lastName, title: fullTitle } = teamMember;
  const commaPosition = fullTitle?.indexOf(',') || 0;

  return {
    ...teamMember,
    fullName: `${firstName} ${lastName}`,
    mainTitle: commaPosition >= 0 ? fullTitle?.substr(0, commaPosition + 1) : fullTitle,
    subTitle: commaPosition >= 0 && fullTitle?.substr(commaPosition + 1),
  };
}

export default function AboutUsPage({ page }) {
  const { t } = useTranslation(['marketing', 'miscellaneous']);
  const executives = page?.member?.filter(teamMember => teamMember?.memberExecutiveTeam);
  const directors = page?.member?.filter(teamMember => !teamMember?.memberExecutiveTeam);
  const heroSection = page?.sections?.find(section => section.name === 'hero');

  const [activeMember, setActiveMember] = useState();
  const imageToSrc = useImageSrcBuilder();

  const modalOpenHandlers = useMemo(() => executives?.map(teamMember => () => {
    setActiveMember(teamMember);
  }), [executives]);

  const onCloseModal = useCallback(() => {
    setActiveMember(undefined);
  }, []);

  const modalContent = useMemo(() => {
    const { fullName, bioImage, mainTitle, subTitle, bio } = buildTeamMember(activeMember);
    return (
      <div className={cx('modalContainer')}>
        <div className={cx('modalHeader')}>
          <div className={cx('modalImageContainer')}>
            <Image
              alt={fullName}
              className={cx('modalImage')}
              src={imageToSrc(bioImage)}
            />
          </div>
          <div className={cx('modalInfoContainer', { noSubtitle: !subTitle })}>
            <Text bold color="blue" size="lg">{fullName}</Text>
            <br />
            <Text as="p" bold className={cx('memberTitle')}>
              {mainTitle}
            </Text>
            {!!subTitle && (
              <Text as="p" bold className={cx('memberTitle')}>
                {subTitle}
              </Text>
            )}
          </div>
        </div>
        <div className={cx('bioContainer')}>
          <Text as="p">
            <Markup source={bio} />
          </Text>
        </div>
      </div>
    );
  }, [activeMember, imageToSrc]);

  return (
    <Page compact name={t('aboutUs.pageTitle')}>
      <Helmet>
        {page?.title && (
          <title>{page.title}</title>
        )}
        {!!page?.meta?.length && generateCMSMetaTags(page.meta, imageToSrc)}
      </Helmet>
      <GradientBanner className={cx('hero')}>
        <Container>
          <Header as="h1" color="white" elongated uppercase>
            <Markup source={heroSection?.title} />
          </Header>
        </Container>
      </GradientBanner>
      <Container className={cx('aboutContentContainer')}>
        {!!heroSection && (
          <Fragment>
            <FlexGrid>
              <FlexGrid.Column tablet={6}>
                <Header as="h2" className={cx('aboutSubHeader')}>{t('aboutUs.subHeader')}</Header>
                <Text as="p">
                  <Markup source={heroSection.description} />
                </Text>
                <br />
                <br />
                <Button
                  as="a"
                  className={cx('getStartedButton')}
                  href={heroSection.ctaUrl}
                  primary
                  size="lg"
                  wide
                >
                  {heroSection.ctaLabel}
                </Button>
              </FlexGrid.Column>
              <FlexGrid.Column tablet={6} desktopOffset={2} desktop={4} className={cx('phoneContainer')}>
                <Image
                  alt={heroSection.imageAlt}
                  src={imageToSrc(heroSection.image)}
                />
              </FlexGrid.Column>
            </FlexGrid>
            <br />
            <br />
            <br />
          </Fragment>
        )}
        {page?.pressCoverage.length && (
          <FlexGrid className={cx('quotesContainer')}>
            {page.pressCoverage.map(quote => (
              <FlexGrid.Column tablet={4} className={cx('quoteContent')} key={quote.content}>
                <Text as="p" className={cx('quoteText')}>
                  <Markup source={quote.content} />
                </Text>
                <div className={cx('redDivider')} />
                <br />
                <Image
                  alt={quote.alt}
                  className={cx('quoteLogo')}
                  src={imageToSrc(quote.image)}
                />
              </FlexGrid.Column>
            ))}
          </FlexGrid>
        )}
        {(!!executives?.length || !!directors?.length) && (
          <Fragment>
            <div id="team" className={cx('leadershipTeamContainer')}>
              <Header as="h2" className={cx('aboutSubHeader')}>{t('aboutUs.leadershipTeam')}</Header>
              <br />
              {!!executives?.length && (
                <Modal
                  content={modalContent}
                  forceIsShowing={!!activeMember}
                  onClose={onCloseModal}
                  size="sm"
                >
                  <FlexGrid>
                    {executives.map((member, idx) => {
                      const { fullName, bioImage, mainTitle, subTitle } = buildTeamMember(member);
                      return (
                        <FlexGrid.Column
                          className={cx('leadershipTeamMember')}
                          key={fullName}
                          onClick={modalOpenHandlers[idx]}
                          mobile={6}
                          tablet={4}
                          desktop={3}
                        >
                          <div className={cx('hoverContent')}>
                            <Image
                              alt={fullName}
                              className={cx('memberImage')}
                              src={imageToSrc(bioImage)}
                            />
                          </div>
                          <br />
                          <Text bold color="blue" size="lg">{fullName}</Text>
                          <br />
                          <Text as="p" bold className={cx('memberTitle')}>
                            {mainTitle}
                          </Text>
                          {!!subTitle && (
                            <Text as="p" bold className={cx('memberTitle')}>
                              {subTitle}
                            </Text>
                          )}
                        </FlexGrid.Column>
                      );
                    })}
                  </FlexGrid>
                </Modal>
              )}
            </div>
            {!!directors?.length && (
              <div className={cx('directorTeamContainer')}>
                <FlexGrid>
                  {directors.map((director) => {
                    const fullName = `${director.firstName} ${director.lastName}`;
                    return (
                      <FlexGrid.Column
                        className={cx('directorTeamMember')}
                        key={director.title}
                        mobile={6}
                        tablet={4}
                        desktop={3}
                      >
                        <div className={cx('hoverContent')}>
                          <a
                            href={director.link}
                            target="_blank"
                            rel="noreferrer noopener"
                            name={t('aboutUs.linkedInLink', { ns: 'marketing', fullName })}
                          >
                            <Image
                              alt=""
                              className={cx('memberImage')}
                              src={imageToSrc(director.bioImage)}
                            />
                          </a>
                        </div>
                        <br />
                        <Text bold color="blue" size="lg">{fullName}</Text>
                        <br />
                        <Text as="p" bold className={cx('memberTitle')}>
                          {director.title}
                        </Text>
                      </FlexGrid.Column>
                    );
                  })}
                </FlexGrid>
              </div>
            )}
          </Fragment>
        )}
      </Container>
      <div className={cx('learnMoreContainer')}>
        <Container>
          <Header as="h2" className={cx('aboutSubHeader')}>
            {t('actions.learnMore', { ns: 'miscellaneous' })}
          </Header>
          <Text as="p">
            {t('aboutUs.pressPageDescription')}
          </Text>
          <br />
          <br />
          <Button
            as="a"
            href="/newsroom"
            primary
            size="lg"
            wide
          >
            {t('navigation.inTheNews', { ns: 'miscellaneous' })}
          </Button>
        </Container>
      </div>
    </Page>
  );
}

AboutUsPage.propTypes = {
  page: AboutUsPagePropType,
};

AboutUsPage.defaultProps = {
  page: undefined,
};
